import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchMessages,
  fetchMessageDetail,
  fetchManualRoomOptions,
  markMessageResponded,
  closeMessageNoTicket,
  queueMessageMediaDownload,
  manualEntry,
  type ManualEntryPayload,
} from '@/services/api/messages'

export function useMessageList(params: {
  page?: number
  page_size?: number
  keyword?: string
  message_status?: string
  binding_status?: string
  sort_by?: string
  sort_order?: string
}) {
  return useQuery({
    queryKey: ['messages', 'list', params],
    queryFn: () => fetchMessages(params),
    staleTime: 15_000,
  })
}

export function useMessageDetail(messageId: string | null) {
  return useQuery({
    queryKey: ['messages', 'detail', messageId],
    queryFn: () => fetchMessageDetail(messageId!),
    enabled: !!messageId,
  })
}

export function useManualRoomOptions(enabled = true) {
  return useQuery({
    queryKey: ['messages', 'manual-room-options'],
    queryFn: () => fetchManualRoomOptions(),
    enabled,
    staleTime: 60_000,
  })
}

export function useMarkResponded() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, note }: { messageId: string; note?: string }) =>
      markMessageResponded(messageId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useCloseNoTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, reason }: { messageId: string; reason: string }) =>
      closeMessageNoTicket(messageId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages'] })
    },
  })
}

export function useQueueMessageMediaDownload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, mediaUid }: { messageId: string; mediaUid: string }) =>
      queueMessageMediaDownload(messageId, mediaUid),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['messages', 'detail', variables.messageId] })
      qc.invalidateQueries({ queryKey: ['messages'] })
    },
  })
}

export function useManualEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ManualEntryPayload) => manualEntry(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages'] })
    },
  })
}
